import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Gauge, RefreshCw, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { fetchInflationExpectations } from "@/lib/api";

const num = (v) => (v == null ? "—" : Number(v).toFixed(2));

// rising inflation expectations = red (inflationary), falling = green
function Delta({ v }) {
  if (v == null) return <span className="text-zinc-700 tabular-nums text-[9px]">--</span>;
  const cls = v > 0 ? "text-neg" : v < 0 ? "text-pos" : "text-zinc-600";
  const Arrow = v > 0 ? ArrowUp : v < 0 ? ArrowDown : Minus;
  return (
    <span className={`inline-flex items-center gap-0.5 tabular-nums text-[9px] ${cls}`}>
      <Arrow size={8} strokeWidth={2.5} />
      {Math.abs(v).toFixed(2)}
    </span>
  );
}

const TYPE_DOT = {
  market: "text-pos",
  model: "text-amber-term",
  survey: "text-sky-400",
};

function SourceBlock({ s }) {
  return (
    <div className="px-3 py-2 border-b border-zinc-900 last:border-0" data-testid={`infexp-src-${s.key}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] uppercase tracking-widest text-zinc-300 flex items-center gap-1.5">
          <span className={TYPE_DOT[s.type] || "text-zinc-500"}>●</span>
          {s.label}
          {s.region === "EA" && <span className="text-zinc-600">· EA</span>}
        </span>
        <span className="text-[9px] uppercase tracking-widest text-zinc-600">{s.type} · {s.freq[0]}</span>
      </div>
      <div className="grid grid-cols-4 gap-x-2 gap-y-1.5">
        {s.points.map((p) => (
          <div key={p.horizon} className="flex flex-col" data-testid={`infexp-${s.key}-${p.horizon.toLowerCase()}`}>
            <span className="text-[9px] uppercase tracking-wider text-zinc-500 leading-none">{p.horizon}</span>
            <span className="text-[13px] font-semibold tabular-nums text-zinc-100 leading-tight">{num(p.value)}</span>
            <Delta v={p.change} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function InflationExpectationsCard() {
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const q = useQuery({
    queryKey: ["infl-expectations"],
    queryFn: () => fetchInflationExpectations(false),
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  });
  const d = q.data;

  const refresh = async () => {
    setRefreshing(true);
    try {
      const fresh = await fetchInflationExpectations(true);
      qc.setQueryData(["infl-expectations"], fresh);
    } catch (e) {
      /* keep prior */
    } finally {
      setRefreshing(false);
    }
  };
  const busy = q.isFetching || refreshing;

  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="infl-expectations-card">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 whitespace-nowrap">
          <Gauge size={11} strokeWidth={1.5} className="text-amber-term" /> Infl. Exp. Term Structure
        </span>
        <div className="flex items-center gap-2">
          {d?.as_of && <span className="text-[11px] uppercase tracking-widest text-zinc-600">{d.as_of}</span>}
          <button
            onClick={refresh}
            disabled={busy}
            data-testid="infexp-refresh-btn"
            className="flex items-center gap-1 text-zinc-500 hover:text-amber-term transition-colors disabled:opacity-50"
          >
            <RefreshCw size={11} strokeWidth={1.5} className={busy ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {q.isLoading ? (
        <div className="h-[180px] flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">
          Loading expectations…
        </div>
      ) : q.isError ? (
        <div className="h-[180px] flex items-center justify-center text-neg text-[11px] uppercase tracking-widest">
          Expectations unavailable
        </div>
      ) : (
        <div>
          {d.stale && (
            <div className="px-3 pt-1.5 text-[9px] uppercase tracking-widest text-amber-term">Stale (cached)</div>
          )}
          {d.sources?.map((s) => (
            <SourceBlock key={s.key} s={s} />
          ))}
          <p className="px-3 py-2 text-[11px] leading-relaxed text-zinc-600">
            All values % annual inflation, uniform horizons per source. Rising = red. Sources: Cleveland Fed model, FRED TIPS breakevens, UMich, NY Fed SCE, ECB SPF.
          </p>
        </div>
      )}
    </div>
  );
}
